import { useState, useEffect } from 'react';
import { safeFormat } from '@/lib/safeDate';
import { useKommoSync } from '@/hooks/useKommoSync';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, RefreshCw, CheckCircle2, XCircle, AlertCircle, Wifi, WifiOff, Play } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface SyncHistoryRow {
  id: string;
  kommo_lead_id: string;
  contact_id: string | null;
  case_id: string | null;
  status: string;
  message: string | null;
  created_at: string;
  raw_data: Record<string, unknown> | null;
}

export default function KommoSync() {
  const {
    isTesting,
    isSyncing,
    lastResult,
    syncProgress,
    testConnection,
    syncAll,
    getSyncHistory,
  } = useKommoSync();

  const [history, setHistory] = useState<SyncHistoryRow[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'unknown' | 'connected' | 'error'>('unknown');

  // Load history on mount
  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    setIsLoadingHistory(true);
    const data = await getSyncHistory(50);
    setHistory(data as SyncHistoryRow[]);
    setIsLoadingHistory(false);
  };

  const handleTestConnection = async () => {
    const result = await testConnection();
    setConnectionStatus(result.success ? 'connected' : 'error');
  };

  const handleSync = async () => {
    await syncAll();
    await loadHistory();
  };

  // Count stats from history
  const successCount = history.filter(h => h.status === 'success').length;
  const errorCount = history.filter(h => h.status === 'error').length;
  const totalCount = history.length;

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Sincronização Kommo</h1>
          <p className="text-muted-foreground">
            Sincronize leads do pipeline Asilo do Kommo para o sistema
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          {connectionStatus === 'connected' && (
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
              <Wifi className="w-3 h-3 mr-1" />
              Conectado
            </Badge>
          )}
          {connectionStatus === 'error' && (
            <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
              <WifiOff className="w-3 h-3 mr-1" />
              Erro na conexão
            </Badge>
          )}
        </div>
      </div>

      {/* Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Test Connection Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Testar Conexão</CardTitle>
            <CardDescription>
              Verifique se as credenciais do Kommo estão corretas
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={handleTestConnection} 
              disabled={isTesting}
              variant="outline"
              className="w-full"
            >
              {isTesting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Testando...
                </>
              ) : (
                <>
                  <Wifi className="w-4 h-4 mr-2" />
                  Testar Conexão
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Sync Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Sincronização Completa</CardTitle>
            <CardDescription>
              Importa todos os leads do pipeline Asilo
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={handleSync} 
              disabled={isSyncing}
              className="w-full"
            >
              {isSyncing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {syncProgress || 'Sincronizando...'}
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  Iniciar Sincronização
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Stats Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Estatísticas</CardTitle>
            <CardDescription>
              Resumo das importações
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <div className="text-2xl font-bold text-foreground">{totalCount}</div>
                <div className="text-xs text-muted-foreground">Total</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600">{successCount}</div>
                <div className="text-xs text-muted-foreground">Sucesso</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-red-600">{errorCount}</div>
                <div className="text-xs text-muted-foreground">Erros</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Last Sync Result */}
      {lastResult && (
        <Card className={lastResult.success ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}>
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              {lastResult.success ? (
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              ) : (
                <XCircle className="w-5 h-5 text-red-600" />
              )}
              <div>
                <p className={`font-medium ${lastResult.success ? 'text-green-800' : 'text-red-800'}`}>
                  {lastResult.success ? 'Operação concluída com sucesso' : 'Erro na operação'}
                </p>
                {lastResult.message && (
                  <p className={`text-sm ${lastResult.success ? 'text-green-700' : 'text-red-700'}`}>
                    {lastResult.message}
                  </p>
                )}
                {lastResult.error && (
                  <p className="text-sm text-red-700">{lastResult.error}</p>
                )}
                {lastResult.stats && (
                  <p className="text-sm text-green-700">
                    Processados: {lastResult.stats.total_processed} | 
                    Sucesso: {lastResult.stats.success_count} | 
                    Erros: {lastResult.stats.error_count}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* History */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Histórico de Importações</CardTitle>
              <CardDescription>
                Últimas 50 sincronizações
              </CardDescription>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={loadHistory}
              disabled={isLoadingHistory}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoadingHistory ? 'animate-spin' : ''}`} />
              Atualizar
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoadingHistory ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : history.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
              <AlertCircle className="w-8 h-8 mb-2" />
              <p>Nenhuma importação encontrada</p>
              <p className="text-sm">Inicie uma sincronização para ver o histórico</p>
            </div>
          ) : (
            <ScrollArea className="h-[400px]">
              <div className="space-y-2">
                {history.map((row) => (
                  <div 
                    key={row.id}
                    className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      {row.status === 'success' ? (
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-600" />
                      )}
                      <div>
                        <p className="font-medium text-sm">
                          Lead #{row.kommo_lead_id}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {row.message || (row.status === 'success' ? 'Importado com sucesso' : 'Erro na importação')}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge variant={row.status === 'success' ? 'default' : 'destructive'} className="text-xs">
                        {row.status === 'success' ? 'Sucesso' : 'Erro'}
                      </Badge>
                      <p className="text-xs text-muted-foreground mt-1">
                        {safeFormat(new Date(row.created_at), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
